import * as React from 'react';
import { useTranslation } from 'react-i18next'
import { Checkbox, ListItemText, MenuItem, TextField } from "@mui/material";
import * as Yup from 'yup';
import { addFilter } from "../../../utils/FilterUtils";
import { useSetRecoilState } from 'recoil';
import { STATE_VOICES_ACTIVE_FILTERS } from '../constants/ClustersRecoil';
import AsyncAutocomplete from "../../../component/AsyncAutocomplete";
import {
    ID_ACCESSORIES,
    ID_BE_FREE_EVO, ID_CAR_BOX, ID_CAR_CLOUD,
    ID_COMMISSIONS,
    ID_DAMAGE, ID_FLEX_RENT,
    ID_FUEL, ID_VOUCHERS
} from "../../mobilitystore/constants/PrintoutTypes";
import { ID_CHECKIN, ID_CHECKOUT } from "../constants/AppliedOn";
import {
    ID_FILTER_APPLIEDON,
    ID_FILTER_PRINTOUTTYPE,
    ID_FILTER_SEGMENT,
    ID_FILTER_SPECIAL,
    ID_FILTER_VOICE
} from "../constants/VoicesFilters";
import { useClusterVoicesRepository } from "../../../api/repositories/ClusterVoicesRepository";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {useState} from "react";
import {CORPORATE, REPLACEMENT, RETAIL, SUBSCRIPTION, TOUR_OPERATOR} from "../../mobilitystore/constants/Segments";

function VoicesFilter({legalEntity, dealer}: {legalEntity: string, dealer: string}) {
    const { t } = useTranslation('finance', { keyPrefix: 'clusters' });
    const setFilterDefs = useSetRecoilState(STATE_VOICES_ACTIVE_FILTERS);
    const { getAllVoiceItems } = useClusterVoicesRepository();
    const [voiceSelectDisabled, setVoiceSelectDisabled] = useState(false);

    const schema = Yup.object().shape({
        appliedOn: Yup.string(),
        printoutType: Yup.array(),
        special: Yup.string(),
        voice: Yup.object().nullable(),
        segment: Yup.string().nullable()
    });

    const defaultValues = {
        appliedOn: '',
        printoutType: [],
        special: '',
        voice: null,
        segment: ''
    };


    const renderAppliedOnField = (field: any) => (
            <TextField fullWidth
                       select
                       label={t('filters.voices.applied-on')}
                       value={field.value}
                       onChange={field.onChange}
            >
                {appliedOn.map((option: any,) => (
                        <MenuItem key={option.value}
                                  value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    );

    const renderPrintoutTypeField = (field: any) => (
            <TextField fullWidth
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) =>
                    selected
                        .map((s: any) => printoutTypes.find((ss) => ss.value === s)!.label)
                        .join(", "),
            }}
            label={t("filters.voices.printout-type")}
            value={field.value}
            onChange={(e) => {
             const selectedCount = e.target.value.length;
             field.onChange(e);
             setVoiceSelectDisabled(selectedCount > 1);
            }}
        >
            {printoutTypes.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1} />
                    <ListItemText primary={status.label} />
                </MenuItem>
            ))}
        </TextField>
    );

    const renderSpecialField = (field: any) => (
            <TextField fullWidth
                       select
                       label={t('filters.voices.special')}
                       value={field.value}
                       onChange={field.onChange}
            >
                {special.map((option: any) => (
                        <MenuItem key={option.value}
                                  value={option.value}>
                            {option.label}
                        </MenuItem>
                ))}
            </TextField>
    );

    const renderVoiceField = (field: any, error: any, state: any, values: any) => (
            <AsyncAutocomplete
                    id={"voice"}
                    initialValues={true}
                    callback={(it) => getAllVoiceItems(values['printoutType'], it, legalEntity, dealer).then(it => it.result)}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField
                            {...params}
                            label={t('filters.voices.voice')}
                    />}
                    disabled={voiceSelectDisabled}
            />
    );


    const renderSegmentField = (field: any) => (
        <TextField fullWidth
                   select
                   label={t('filters.voices.segment')}
                   value={field.value}
                   onChange={field.onChange}
        >
            {segments.map((option: any) => (
                <MenuItem key={option.value}
                          value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );

    const fields = {
        special: renderSpecialField,
        segment: renderSegmentField
    }

    const mainFields ={
        appliedOn: renderAppliedOnField,
        printoutType: renderPrintoutTypeField,
        voice: renderVoiceField
    }

    const printoutTypes = [
        {
            label: t("printout-types." + ID_ACCESSORIES),
            value: ID_ACCESSORIES,
            selected: true
        },
        {
            label: t("printout-types." + ID_FUEL),
            value: ID_FUEL
        },
        {
            label: t("printout-types." + ID_COMMISSIONS),
            value: ID_COMMISSIONS
        },
        {
            label: t("printout-types." + ID_DAMAGE),
            value: ID_DAMAGE
        },
        {
            label: t("printout-types." + ID_VOUCHERS),
            value: ID_VOUCHERS
        },
        {
            label: t("printout-types." + ID_BE_FREE_EVO),
            value: ID_BE_FREE_EVO
        },
        {
            label: t("printout-types." + ID_CAR_CLOUD),
            value: ID_CAR_CLOUD
        },
        {
            label: t("printout-types." + ID_FLEX_RENT),
            value: ID_FLEX_RENT
        },
        {
            label: t("printout-types." + ID_CAR_BOX),
            value: ID_CAR_BOX
        }
    ];

    const appliedOn = [
        {
            label: t("applied-on." + ID_CHECKIN),
            value: ID_CHECKIN,
            selected: true
        },
        {
            label: t("applied-on." + ID_CHECKOUT),
            value: ID_CHECKOUT
        },
    ];
    const special = [
        {
            label: t('specials.EXCLUDE_LAST_RENTAL'),
            value: "EXCLUDE_LAST_RENTAL"
        }
    ];

    const segments = [
        {
            label: t("segments." + TOUR_OPERATOR),
            value: TOUR_OPERATOR,
            selected: true
        },
        {
            label: t("segments." + REPLACEMENT),
            value: REPLACEMENT
        },
        {
            label: t("segments." + CORPORATE),
            value: CORPORATE
        },
        {
            label: t("segments." + RETAIL),
            value: RETAIL
        },
        {
            label: t("segments." + SUBSCRIPTION),
            value: SUBSCRIPTION
        }
    ];

    const submit = (data: any) => {
        addFilter(setFilterDefs, ID_FILTER_VOICE, data.voice);
        addFilter(setFilterDefs, ID_FILTER_APPLIEDON, data.appliedOn);
        addFilter(setFilterDefs, ID_FILTER_PRINTOUTTYPE, data.printoutType);
        addFilter(setFilterDefs, ID_FILTER_SPECIAL, data.special);
        addFilter(setFilterDefs, ID_FILTER_SEGMENT, data.segment);
    }

    const reset = () => {
        setFilterDefs([]);
    }

    return (
            <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                            mainFields={mainFields}
                            onSubmit={submit}
                            onReset={reset}
            />
    )

}

export default VoicesFilter;